/* DailyFlow — Notion-inspired daily todo list with automatic rollover.
 * Items you don't finish today are silently carried to tomorrow, with a subtle
 * "flyttad X dagar" badge so you can see what keeps slipping. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GripVertical, Plus, Check, Trash2, ChevronDown, ChevronRight, Loader2, RefreshCw,
  Repeat, MoreHorizontal, Calendar, Feather, X, Moon, Bell, Heart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function rolloverLabel(n) {
  if (!n) return null;
  if (n === 1) return 'flyttad 1 dag';
  return `flyttad ${n} dagar`;
}

function ItemRow({ item, onToggle, onDelete, onEdit, onToggleHabit, onSetHabitTime, onOpenLingerModal, onDragStart, onDragOver, onDrop, dragging }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(item.text); }, [item.text]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    const t = draft.trim();
    if (!t || t === item.text) { setEditing(false); setDraft(item.text); return; }
    onEdit(item.id, t);
    setEditing(false);
  };

  const isDone = !!item.done_at;
  const rolled = rolloverLabel(item.rollover_count);
  const isLingering = (item.rollover_count || 0) >= 7 && !isDone;
  const isHabit = !!item.is_habit;

  return (
    <div
      draggable={!editing && !isDone}
      onDragStart={() => onDragStart?.(item.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver?.(item.id); }}
      onDrop={() => onDrop?.(item.id)}
      data-testid={`daily-item-${item.id}`}
      className={`group flex items-start gap-2 py-1.5 px-1 rounded-md transition ${
        dragging === item.id ? 'opacity-40' : 'opacity-100'
      } ${isDone ? 'text-slate-400' : 'text-slate-800'} hover:bg-slate-50`}
    >
      {!isDone ? (
        <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-1.5 opacity-0 group-hover:opacity-100 cursor-grab flex-shrink-0" />
      ) : (
        <span className="w-3.5 h-3.5 mt-1.5 flex-shrink-0" />
      )}
      <button
        type="button"
        onClick={() => onToggle(item.id, !isDone)}
        data-testid={`daily-toggle-${item.id}`}
        aria-label={isDone ? 'Markera oavklarad' : 'Markera klar'}
        className={`mt-1 w-4 h-4 rounded-[5px] border flex items-center justify-center flex-shrink-0 transition ${
          isDone
            ? 'bg-slate-800 border-slate-800 text-white'
            : isHabit
            ? 'border-emerald-400 hover:border-emerald-600 bg-white'
            : 'border-slate-300 hover:border-slate-500 bg-white'
        }`}
      >
        {isDone && <Check className="w-3 h-3" strokeWidth={3} />}
      </button>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setEditing(false); setDraft(item.text); }
            }}
            maxLength={500}
            className="w-full text-sm bg-transparent border-none outline-none focus:ring-0 p-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => !isDone && setEditing(true)}
            className={`text-left w-full text-sm ${isDone ? 'line-through' : ''}`}
          >
            {item.text}
          </button>
        )}
        <div className="flex flex-wrap gap-1 mt-0.5 items-center">
          {isHabit && (
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
              <Repeat className="w-2.5 h-2.5" /> vana
            </span>
          )}
          {isHabit && item.habit_time && (
            <span className="text-[10px] text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
              {item.habit_time === 'morning' && '🌅 morgon'}
              {item.habit_time === 'lunch' && '☀️ lunch'}
              {item.habit_time === 'evening' && '🌙 kväll'}
            </span>
          )}
          {isHabit && !isDone && (
            <select
              value={item.habit_time || ''}
              onChange={(e) => onSetHabitTime?.(item.id, e.target.value)}
              data-testid={`habit-time-${item.id}`}
              className="text-[10px] bg-transparent text-slate-400 hover:text-sky-600 border-0 focus:outline-none cursor-pointer opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">tid…</option>
              <option value="morning">🌅 morgon</option>
              <option value="lunch">☀️ lunch</option>
              <option value="evening">🌙 kväll</option>
            </select>
          )}
          {rolled && !isDone && (
            <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full inline-block">
              ↻ {rolled}
            </span>
          )}
          {isLingering && (
            <button
              type="button"
              onClick={() => onOpenLingerModal?.(item)}
              data-testid={`daily-linger-${item.id}`}
              className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"
            >
              🕊 dröjt länge · knuffa mig
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onToggleHabit?.(item.id, !isHabit)}
        data-testid={`daily-habit-toggle-${item.id}`}
        aria-label={isHabit ? 'Ta bort som vana' : 'Markera som återkommande vana'}
        title={isHabit ? 'Ta bort som vana' : 'Gör till återkommande vana'}
        className={`opacity-0 group-hover:opacity-100 mt-1 ${isHabit ? 'text-emerald-500 opacity-100' : 'text-slate-300 hover:text-emerald-500'}`}
      >
        <Repeat className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(item.id)}
        data-testid={`daily-delete-${item.id}`}
        aria-label="Ta bort"
        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 mt-1"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function DailyFlow() {
  const [active, setActive] = useState([]);
  const [done, setDone] = useState([]);
  const [rolledCount, setRolledCount] = useState(0);
  const [today, setToday] = useState('');
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [heatmap, setHeatmap] = useState({ days: [], habits: [] });
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [lingerItem, setLingerItem] = useState(null);
  const [atRiskHabits, setAtRiskHabits] = useState([]);
  const [snoozed, setSnoozed] = useState([]);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const dragOrderRef = useRef([]);

  const loadHeatmap = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/daily-flow/habits/heatmap?days=7`, { withCredentials: true });
      setHeatmap({ days: data.days || [], habits: data.habits || [] });
    } catch { /* noop */ }
  }, []);

  const loadAtRisk = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/daily-flow/habits/at-risk`, { withCredentials: true });
      setAtRiskHabits(data.items || []);
    } catch { /* noop */ }
  }, []);

  const loadSnoozed = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/daily-flow/snoozed`, { withCredentials: true });
      setSnoozed(data.items || []);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/daily-flow/today`, { withCredentials: true });
      setActive(data.active || []);
      setDone(data.done || []);
      setRolledCount(data.rolled_over || 0);
      setToday(data.today || '');
      if (data.rolled_over > 0) {
        toast(`${data.rolled_over} punkt${data.rolled_over === 1 ? '' : 'er'} flyttades till idag ↻`, { duration: 3500 });
      }
      await loadHeatmap();
      await loadAtRisk();
      await loadSnoozed();
    } catch {
      toast.error('Kunde inte ladda listan');
    } finally {
      setLoading(false);
    }
  }, [loadHeatmap, loadAtRisk, loadSnoozed]);

  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    const t = newText.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/daily-flow/items`, { text: t }, { withCredentials: true });
      setActive((a) => [...a, data]);
      setNewText('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (id, done_) => {
    // optimistic
    if (done_) {
      const it = active.find((x) => x.id === id);
      if (!it) return;
      setActive((a) => a.filter((x) => x.id !== id));
      setDone((d) => [{ ...it, done_at: new Date().toISOString(), done_day: today }, ...d]);
    } else {
      const it = done.find((x) => x.id === id);
      if (!it) return;
      setDone((d) => d.filter((x) => x.id !== id));
      setActive((a) => [...a, { ...it, done_at: null, done_day: null }]);
    }
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { done: done_ }, { withCredentials: true });
    } catch {
      toast.error('Kunde inte uppdatera');
      load();
    }
  };

  const editText = async (id, text) => {
    setActive((a) => a.map((x) => (x.id === id ? { ...x, text } : x)));
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { text }, { withCredentials: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
      load();
    }
  };

  const del = async (id) => {
    setActive((a) => a.filter((x) => x.id !== id));
    setDone((d) => d.filter((x) => x.id !== id));
    try {
      await axios.delete(`${API}/daily-flow/items/${id}`, { withCredentials: true });
    } catch {
      toast.error('Kunde inte ta bort');
      load();
    }
  };

  const toggleHabit = async (id, isHabit) => {
    setActive((a) => a.map((x) => (x.id === id ? { ...x, is_habit: isHabit } : x)));
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { is_habit: isHabit }, { withCredentials: true });
      toast(isHabit ? 'Markerad som vana — syns i veckospåret 🌱' : 'Inte längre en vana', { duration: 2500 });
      loadHeatmap();
    } catch {
      toast.error('Kunde inte uppdatera');
      load();
    }
  };

  const snooze = async (id, days) => {
    setActive((a) => a.filter((x) => x.id !== id));
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { snooze_days: days }, { withCredentials: true });
      toast(`Vi låter den vila ${days} dagar 💜`, { duration: 3000 });
      loadSnoozed();
    } catch {
      toast.error('Kunde inte snooza');
      load();
    }
    setLingerItem(null);
  };

  const snoozeUntil = async (id, dateStr) => {
    setActive((a) => a.filter((x) => x.id !== id));
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { snooze_until: dateStr }, { withCredentials: true });
      toast(`Vilar tills ${dateStr} 💜`, { duration: 3000 });
      loadSnoozed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte snooza');
      load();
    }
    setLingerItem(null);
  };

  const setHabitTime = async (id, time) => {
    setActive((a) => a.map((x) => (x.id === id ? { ...x, habit_time: time } : x)));
    try {
      await axios.patch(`${API}/daily-flow/items/${id}`, { habit_time: time }, { withCredentials: true });
      if (time) {
        const label = { morning: 'morgon', lunch: 'lunch', evening: 'kväll' }[time];
        toast(`🐾 Jag påminner dig på ${label}`, { duration: 2500 });
      } else {
        toast('Ingen speciell tid — påminner generellt');
      }
    } catch {
      toast.error('Kunde inte spara');
      load();
    }
  };

  const release = async (id) => {
    setLingerItem(null);
    await del(id);
    toast('Släppt taget 🕊 Ingen skam.', { duration: 3500 });
  };

  const wakeSnoozed = async (id) => {
    setSnoozed((s) => s.filter((x) => x.id !== id));
    try {
      await axios.post(`${API}/daily-flow/items/${id}/wake`, {}, { withCredentials: true });
      toast('Väckt tillbaka till idag 🌅', { duration: 2500 });
      load();
    } catch {
      toast.error('Kunde inte väcka');
      loadSnoozed();
    }
  };

  const dismissNudge = async (habitId, silent = false) => {
    setAtRiskHabits((s) => s.filter((x) => x.id !== habitId));
    try {
      await axios.post(`${API}/daily-flow/habits/${habitId}/dismiss-nudge`, {}, { withCredentials: true });
      if (!silent) toast('Låter den ligga kvar 💜', { duration: 2500 });
    } catch { /* noop */ }
  };

  const keepHabit = (habitId) => {
    // Same as dismiss — user affirmed the habit, silence nudge for 3 days
    dismissNudge(habitId, true);
    toast('Fint! Vi håller kvar den 🌱', { duration: 2500 });
  };

  const unmarkHabit = async (habitId) => {
    setAtRiskHabits((s) => s.filter((x) => x.id !== habitId));
    try {
      await axios.patch(`${API}/daily-flow/items/${habitId}`, { is_habit: false }, { withCredentials: true });
      toast('Inte längre en vana — ingen skam 🕊', { duration: 2800 });
      load();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const onDragStart = (id) => { setDragging(id); dragOrderRef.current = active.map((x) => x.id); };
  const onDragOver = (overId) => {
    if (!dragging || dragging === overId) return;
    setActive((a) => {
      const cp = [...a];
      const from = cp.findIndex((x) => x.id === dragging);
      const to = cp.findIndex((x) => x.id === overId);
      if (from === -1 || to === -1) return a;
      const [m] = cp.splice(from, 1);
      cp.splice(to, 0, m);
      return cp;
    });
  };
  const onDrop = async () => {
    setDragging(null);
    const newOrder = active.map((x) => x.id);
    if (JSON.stringify(newOrder) === JSON.stringify(dragOrderRef.current)) return;
    try {
      await axios.post(`${API}/daily-flow/reorder`, { order: newOrder }, { withCredentials: true });
    } catch {
      toast.error('Kunde inte spara ordning');
      load();
    }
  };

  const doneCount = done.length;
  const total = active.length + doneCount;
  const dateLabel = today ? new Date(today + 'T00:00:00').toLocaleDateString('sv-SE', {
    weekday: 'long', day: 'numeric', month: 'long',
  }) : '';

  return (
    <div className="max-w-2xl mx-auto space-y-4 py-2" data-testid="daily-flow-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 border-b border-slate-100 pb-3">
        <div>
          <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Idag-lista</div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {dateLabel || 'Idag'}
          </h1>
          <div className="text-xs text-slate-500 mt-1">
            {total === 0 ? 'Tom lista — börja skriv nedan' : `${doneCount}/${total} klara idag`}
            {rolledCount > 0 && (
              <span className="ml-2 text-amber-600">· ↻ {rolledCount} flyttade från igår</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="daily-refresh"
          className="text-slate-400 hover:text-slate-700 p-1.5"
          aria-label="Uppdatera"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && active.length === 0 && done.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-10">
          <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" /> Laddar…
        </div>
      ) : (
        <>
          {/* Habit-nudge from pet — soft check-in if habit missed 2 days */}
          {atRiskHabits.length > 0 && (
            <div className="space-y-2" data-testid="habit-nudge-section">
              {atRiskHabits.map((h) => (
                <div
                  key={h.id}
                  className="rounded-2xl bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 border border-rose-200 p-4 relative"
                  data-testid={`habit-nudge-${h.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-300 to-amber-300 flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                      🐾
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase font-bold text-rose-600 tracking-wider">
                        Ditt husdjur undrar
                      </div>
                      <p className="text-sm text-slate-800 mt-1">
                        {h.reason === 'time_of_day' ? (
                          <>
                            {h.habit_time === 'morning' && 'Morgonen har passerat och jag har inte sett dig göra '}
                            {h.habit_time === 'lunch' && 'Lunchtiden har gått och jag har inte sett dig göra '}
                            {h.habit_time === 'evening' && 'Kvällen är här och jag saknar '}
                            <span className="font-semibold">&quot;{h.text}&quot;</span>. Vill du fortfarande ha detta som vana? 💜
                          </>
                        ) : (
                          <>
                            Jag har inte sett dig göra <span className="font-semibold">&quot;{h.text}&quot;</span> på{' '}
                            <span className="font-semibold">2 dagar i rad</span>. Vill du fortfarande ha detta som vana? 💜
                          </>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        <button
                          type="button"
                          onClick={() => keepHabit(h.id)}
                          data-testid={`nudge-keep-${h.id}`}
                          className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-full font-medium inline-flex items-center gap-1"
                        >
                          <Heart className="w-3 h-3" /> Ja, behåll
                        </button>
                        <button
                          type="button"
                          onClick={() => unmarkHabit(h.id)}
                          data-testid={`nudge-remove-${h.id}`}
                          className="text-xs bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1 rounded-full font-medium"
                        >
                          Nej, ta bort som vana
                        </button>
                        <button
                          type="button"
                          onClick={() => dismissNudge(h.id)}
                          data-testid={`nudge-dismiss-${h.id}`}
                          className="text-xs bg-white hover:bg-slate-50 border border-slate-200 text-slate-500 px-3 py-1 rounded-full"
                        >
                          Fråga senare
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => dismissNudge(h.id)}
                      className="text-slate-300 hover:text-slate-500 -mr-1"
                      aria-label="Stäng"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Habits weekly heatmap */}
          {heatmap.habits.length > 0 && (
            <div className="rounded-xl bg-gradient-to-br from-emerald-50/60 to-white border border-emerald-100 p-3">
              <button
                type="button"
                onClick={() => setShowHeatmap((v) => !v)}
                data-testid="daily-heatmap-toggle"
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900">
                  <Repeat className="w-3.5 h-3.5" /> Vanor-veckospår
                  <span className="text-[11px] font-normal text-emerald-700/70">({heatmap.habits.length} vanor)</span>
                </div>
                {showHeatmap ? <ChevronDown className="w-3.5 h-3.5 text-emerald-700" /> : <ChevronRight className="w-3.5 h-3.5 text-emerald-700" />}
              </button>
              {showHeatmap && (
                <div className="mt-2 space-y-1.5">
                  {/* header row: weekday initials */}
                  <div className="flex items-center gap-1 pl-[104px] text-[10px] text-emerald-700/60 font-medium">
                    {heatmap.days.map((d) => {
                      const dt = new Date(d + 'T00:00:00');
                      const wd = ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'][dt.getDay()];
                      const isToday = d === today;
                      return (
                        <div key={d} className={`w-6 text-center ${isToday ? 'text-emerald-800 font-bold' : ''}`}>
                          {wd}
                        </div>
                      );
                    })}
                  </div>
                  {heatmap.habits.map((h) => (
                    <div key={h.text} className="flex items-center gap-1" data-testid={`heatmap-row-${h.id}`}>
                      <div className="w-[100px] text-xs text-slate-700 truncate pr-1" title={h.text}>{h.text}</div>
                      {h.cells.map((c, i) => {
                        const isToday = heatmap.days[i] === today;
                        return (
                          <div
                            key={i}
                            className={`w-6 h-6 rounded-md border ${
                              c
                                ? 'bg-emerald-400 border-emerald-500'
                                : 'bg-white border-slate-200'
                            } ${isToday ? 'ring-1 ring-emerald-400' : ''}`}
                            title={`${heatmap.days[i]}${c ? ' · klarat' : ''}`}
                          />
                        );
                      })}
                      <div className="ml-2 text-[10px] text-slate-500 whitespace-nowrap">
                        {h.streak > 0 && <span className="text-emerald-700 font-bold">🔥 {h.streak}d</span>}
                        <span className="ml-1 text-slate-400">· {h.completion_pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Active list */}
          <div className="space-y-0">
            {active.length === 0 && (
              <div className="text-sm text-slate-400 italic py-2 px-1">
                Ingen punkt just nu — vad behöver göras idag? 💜
              </div>
            )}
            {active.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                onToggle={toggle}
                onDelete={del}
                onEdit={editText}
                onToggleHabit={toggleHabit}
                onSetHabitTime={setHabitTime}
                onOpenLingerModal={setLingerItem}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                dragging={dragging}
              />
            ))}
          </div>

          {/* Add new */}
          <div className="flex items-center gap-2 pt-1 px-1">
            <Plus className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !adding) { e.preventDefault(); addItem(); }
              }}
              placeholder="Lägg till en punkt…"
              maxLength={500}
              disabled={adding}
              data-testid="daily-new-input"
              className="flex-1 bg-transparent border-none outline-none focus:ring-0 text-sm text-slate-800 placeholder-slate-400 py-1"
            />
            {newText.trim() && (
              <button
                type="button"
                onClick={addItem}
                disabled={adding}
                data-testid="daily-add-btn"
                className="text-xs text-slate-500 hover:text-slate-800 px-2 py-0.5"
              >
                {adding ? '…' : 'Enter ↵'}
              </button>
            )}
          </div>

          {/* Done section */}
          {done.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowDone((v) => !v)}
                data-testid="daily-toggle-done"
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 mb-1"
              >
                {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Klara idag ({done.length})
              </button>
              {showDone && (
                <div className="space-y-0">
                  {done.map((it) => (
                    <ItemRow
                      key={it.id}
                      item={it}
                      onToggle={toggle}
                      onDelete={del}
                      onEdit={editText}
                      onToggleHabit={toggleHabit}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Snoozed items — 'Vilar just nu' */}
          {snoozed.length > 0 && (
            <div className="pt-3 border-t border-slate-100" data-testid="snoozed-section">
              <button
                type="button"
                onClick={() => setShowSnoozed((v) => !v)}
                data-testid="daily-toggle-snoozed"
                className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-700 mb-1"
              >
                {showSnoozed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <Moon className="w-3 h-3" /> Vilar just nu ({snoozed.length})
              </button>
              {showSnoozed && (
                <div className="space-y-1 rounded-xl bg-violet-50/40 border border-violet-100 p-2">
                  {snoozed.map((s) => {
                    const wakeDate = s.snoozed_until
                      ? new Date(s.snoozed_until + 'T00:00:00').toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })
                      : '';
                    return (
                      <div
                        key={s.id}
                        className="group flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-white transition"
                        data-testid={`snoozed-item-${s.id}`}
                      >
                        <Moon className="w-3.5 h-3.5 text-violet-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-700 truncate">{s.text}</div>
                          <div className="text-[10px] text-violet-600 mt-0.5">
                            Kommer tillbaka {wakeDate}
                            {s.rollover_count > 0 && ` · ↻ ${s.rollover_count} dagar innan`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => wakeSnoozed(s.id)}
                          data-testid={`snoozed-wake-${s.id}`}
                          title="Väck till idag"
                          className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-violet-200 text-violet-700 hover:bg-violet-100 opacity-0 group-hover:opacity-100 flex items-center gap-0.5"
                        >
                          <Bell className="w-2.5 h-2.5" /> Väck
                        </button>
                        <button
                          type="button"
                          onClick={() => del(s.id)}
                          className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 mt-0.5"
                          aria-label="Ta bort"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Hint about rollover */}
          <div className="pt-4 text-[11px] text-slate-400 border-t border-slate-100 leading-relaxed">
            💡 Punkter du inte klarar av idag flyttas automatiskt till imorgon. Efter några dagar syns en liten <span className="text-amber-600">↻ flyttad</span>-etikett så du kan spot:a vad som gärna dröjer. Klicka <Repeat className="w-2.5 h-2.5 inline text-emerald-500" />-ikonen på en punkt för att göra den till en <span className="text-emerald-700">återkommande vana</span>.
          </div>
        </>
      )}

      {/* Slipp-lista / linger modal */}
      {lingerItem && (
        <div className="fixed inset-0 z-[110] bg-black/40 flex items-center justify-center p-4" onClick={() => setLingerItem(null)}>
          <div
            className="bg-white rounded-2xl max-w-md w-full p-5 border border-violet-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="linger-modal"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Feather className="w-5 h-5 text-violet-600" />
                <div className="text-lg font-semibold text-violet-900">Släpp taget?</div>
              </div>
              <button
                type="button"
                onClick={() => setLingerItem(null)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Stäng"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-slate-700 mb-1">
              &quot;<span className="font-medium">{lingerItem.text}</span>&quot; har följt med dig i{' '}
              <span className="font-bold text-violet-700">{lingerItem.rollover_count} dagar</span>.
            </p>
            <p className="text-xs text-slate-500 mb-4 italic">
              Ingen skam. Ibland dröjer saker för att de behöver en fast tid, ibland för att de inte längre är dina. Hur känns det?
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => snooze(lingerItem.id, 3)}
                data-testid="linger-snooze"
                className="w-full flex items-center gap-2 rounded-xl border border-violet-200 hover:bg-violet-50 p-3 text-left"
              >
                <Calendar className="w-4 h-4 text-violet-500 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-violet-900">Låt den vila i 3 dagar</div>
                  <div className="text-[11px] text-slate-500">Kommer tillbaka snart</div>
                </div>
              </button>
              <div className="rounded-xl border border-violet-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-violet-500 flex-shrink-0" />
                  <div className="text-sm font-medium text-violet-900">Vila till specifikt datum</div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                    max={new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10)}
                    id="linger-date-input"
                    data-testid="linger-date-input"
                    className="flex-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = document.getElementById('linger-date-input')?.value;
                      if (val) snoozeUntil(lingerItem.id, val);
                      else toast('Välj ett datum först');
                    }}
                    data-testid="linger-snooze-date"
                    className="text-xs px-3 py-1 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-medium"
                  >
                    Vila
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => release(lingerItem.id)}
                data-testid="linger-release"
                className="w-full flex items-center gap-2 rounded-xl border border-rose-200 hover:bg-rose-50 p-3 text-left"
              >
                <Feather className="w-4 h-4 text-rose-500 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-rose-900">Släpp taget</div>
                  <div className="text-[11px] text-slate-500">Tar bort punkten helt — ingen skam 🕊</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setLingerItem(null)}
                data-testid="linger-keep"
                className="w-full rounded-xl bg-slate-50 hover:bg-slate-100 p-3 text-sm text-slate-700"
              >
                Låt den ligga kvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
